import { lazy, Suspense } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';

import { Loader } from '@/components/Loader';
import { AudiencePage } from '@/features/audiencias/routes/AudiencePage';
const AudienceChannelSourcesPage = lazy(
  () => import('@/features/audiencias/routes/AudienceChannelSourcesPage')
);
import { BroadcastScheduleProgramContextProvider } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { EconomicalValuesPage } from '@/features/economic-values/routes/EconomicalValuesPage';
const EconomicalValueChannelSourcesPage = lazy(
  () => import('@/features/economic-values/routes/EconomicalValueChannelSourcesPage')
);
import FeaturedPeoplePage from '@/features/featured-people/routes/FeaturedPeoplePage';
import { PERMISSIONS, PERMISSIONS_MENU, RequirePlatformRouteAuth } from '@/lib/auth';
import { AppProvider } from '@/providers/app';

import { AVRouteEnum } from './AVRouteEnum';
import { MediaManagerRouteEnum } from './MediaManagerRouteEnum';

const ParentBroadcastersPage = lazy(
  () => import('@/features/parent-broadcasters/routes/ParentBroadcastersPage')
);
const ProductionCenterPage = lazy(
  () => import('@/features/production-center/routes/ProductionCenterPage')
);
const LogosPage = lazy(() => import('@/features/logos/routes/LogosPage'));
const BroadcastersPage = lazy(() => import('@/features/broadcasters/routes/BroadcastersPage'));
const RecorderPage = lazy(() => import('@/features/recorder/routes/RecorderPage'));
const BroadcastSchedulePage = lazy(
  () => import('@/features/broadcast-schedule/routes/BroadcastSchedulePage')
);
const BroadcastScheduleProgramPage = lazy(
  () => import('@/features/broadcast-schedule-program/routes/BroadcastScheduleProgramPage')
);
const SegmentsPage = lazy(() => import('@/features/segments/routes/SegmentsPage'));
const ClipsPage = lazy(() => import('@/features/clips/routes/ClipsPage'));
const ProgramPage = lazy(() => import('@/features/programs/routes/ProgramPage'));
const ChannelRecordingsPage = lazy(
  () => import('@/features/channel-recordings/routes/ChannelRecordingsPage')
);
const CNMCsPage = lazy(() => import('@/features/cnmcs/routes/CNMCsPage'));
const SupportsPage = lazy(() => import('@/features/supports/routes/SupportsPage'));
const ProfilePage = lazy(() => import('@/features/users/routes/ProfilePage/ProfilePage'));

export const MEDIA_MANAGER_ROUTES: RouteObject = {
  id: MediaManagerRouteEnum.MEDIA_MANAGER,
  path: 'app',
  element: <AppProvider />,
  children: [
    {
      path: '',
      element: <Navigate to={'av'} />,
    },
    {
      id: AVRouteEnum.AV,
      path: 'av',
      element: <RequirePlatformRouteAuth permissions={[PERMISSIONS.MEDIA_MANAGER.ACCESS]} />,
      children: [
        {
          path: '',
          element: <Navigate to={'sources'} />,
        },
        {
          id: AVRouteEnum.SOURCES,
          path: 'sources',
          element: <RequirePlatformRouteAuth permissions={PERMISSIONS_MENU.SOURCES} />,
          children: [
            {
              path: '',
              element: <Navigate to={'production_centers'} />,
            },
            {
              id: AVRouteEnum.PRODUCTION_CENTERS,
              path: 'production_centers',
              element: (
                <RequirePlatformRouteAuth
                  permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_PRODUCTION_CENTER]}
                />
              ),
              children: [
                {
                  path: '',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <ProductionCenterPage />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              id: AVRouteEnum.PARENT_BROADCASTERS,
              path: 'parent_broadcasters',
              element: (
                <RequirePlatformRouteAuth permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_CHANNEL]} />
              ),
              children: [
                {
                  path: '',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <ParentBroadcastersPage />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              id: AVRouteEnum.BROADCASTERS,
              path: 'broadcasters',
              element: (
                <RequirePlatformRouteAuth permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_CHANNEL]} />
              ),
              children: [
                {
                  path: '',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <BroadcastersPage />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
        {
          id: AVRouteEnum.MANAGEMENT,
          path: 'management',
          element: <RequirePlatformRouteAuth permissions={PERMISSIONS_MENU.MANAGEMENT} />,
          children: [
            {
              path: '',
              element: <Navigate to={'recording'} />,
            },
            {
              path: 'recording',
              element: <Outlet />,
              children: [
                {
                  path: '',
                  element: <Navigate to={'schedules'} />,
                },
                {
                  id: AVRouteEnum.SCHEDULE_RECORDINGS,
                  path: 'schedules',
                  element: (
                    <RequirePlatformRouteAuth
                      permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_CHANNEL_RECORDING_SCHEDULE]}
                    />
                  ),
                  children: [
                    {
                      path: '',
                      element: <ChannelRecordingsPage />,
                    },
                  ],
                },
                {
                  id: AVRouteEnum.RECORDINGS,
                  path: 'recordings',
                  element: (
                    <RequirePlatformRouteAuth
                      permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_CHANNEL_RECORDING]}
                    />
                  ),
                  children: [
                    {
                      path: '',
                      element: (
                        <Suspense fallback={<Loader />}>
                          <RecorderPage />
                        </Suspense>
                      ),
                    },
                  ],
                },
              ],
            },
            {
              id: AVRouteEnum.PROGRAMS,
              path: 'programs',
              element: (
                <RequirePlatformRouteAuth permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_PROGRAM]} />
              ),
              children: [
                {
                  path: '',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <ProgramPage />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
        {
          id: AVRouteEnum.ENRICHMENTS,
          path: 'enrichments',
          element: <RequirePlatformRouteAuth permissions={PERMISSIONS_MENU.ENRICHMENTS} />,
          children: [
            {
              path: '',
              element: <Navigate to={'featured_people'} />,
            },
            {
              id: AVRouteEnum.FEATURED_PEOPLE,
              path: 'featured_people',
              element: (
                <RequirePlatformRouteAuth permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_PERSON]} />
              ),
              children: [
                {
                  path: '',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <FeaturedPeoplePage />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              id: AVRouteEnum.LOGOS,
              path: 'logos',
              element: (
                <RequirePlatformRouteAuth permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_LOGO]} />
              ),
              children: [
                {
                  path: '',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <LogosPage />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              id: AVRouteEnum.SUPPORTS,
              path: 'supports',
              element: (
                <RequirePlatformRouteAuth permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_LOGO]} />
              ),
              children: [
                {
                  path: '',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <SupportsPage />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              id: AVRouteEnum.AUDIENCES,
              path: 'audiences',
              element: (
                <RequirePlatformRouteAuth
                  permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_AUDIENCE_DATA]}
                />
              ),
              children: [
                {
                  path: '',
                  element: <Navigate to={'channel-sources'} />,
                },
                {
                  id: AVRouteEnum.AUDIENCE_CHANNEL_SOURCES,
                  path: 'channel-sources',
                  element: (
                    <RequirePlatformRouteAuth
                      permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_AUDIENCE_DATA]}
                    />
                  ),
                  children: [
                    {
                      path: '',
                      element: (
                        <Suspense fallback={<Loader />}>
                          <AudienceChannelSourcesPage />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  id: AVRouteEnum.AUDIENCES_LIST,
                  path: 'upload',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <AudiencePage />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              id: AVRouteEnum.ECONOMIC_VALUE,
              path: 'economic_value',
              element: (
                <RequirePlatformRouteAuth
                  permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_ECONOMIC_VALUE_DATA]}
                />
              ),
              children: [
                {
                  path: '',
                  element: <Navigate to={'channel-sources'} />,
                },
                {
                  id: AVRouteEnum.ECONOMIC_VALUE_CHANNEL_SOURCES,
                  path: 'channel-sources',
                  element: (
                    <RequirePlatformRouteAuth
                      permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_ECONOMIC_VALUE_DATA]}
                    />
                  ),
                  children: [
                    {
                      path: '',
                      element: (
                        <Suspense fallback={<Loader />}>
                          <EconomicalValueChannelSourcesPage />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  id: AVRouteEnum.ECONOMIC_VALUE_UPLOAD,
                  path: 'upload',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <EconomicalValuesPage />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              id: AVRouteEnum.CNMC_REPORTS,
              path: 'cnmc_reports',
              element: (
                <RequirePlatformRouteAuth
                  permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_CNMC_REPORT]}
                />
              ),
              children: [
                {
                  path: '',
                  element: (
                    <Suspense fallback={<Loader />}>
                      <CNMCsPage />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: MediaManagerRouteEnum.REVIEW,
      path: 'review',
      element: (
        <BroadcastScheduleProgramContextProvider>
          <RequirePlatformRouteAuth permissions={PERMISSIONS_MENU.REVIEW} />
        </BroadcastScheduleProgramContextProvider>
      ),
      children: [
        {
          path: '',
          element: <Navigate to={'broadcast_schedule'} />,
        },
        {
          id: MediaManagerRouteEnum.BROADCAST_SCHEDULE,
          path: 'broadcast_schedule',
          element: (
            <RequirePlatformRouteAuth
              permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_PROGRAM_BROADCAST]}
            />
          ),
          children: [
            {
              path: '',
              element: (
                <Suspense fallback={<Loader />}>
                  <BroadcastSchedulePage />
                </Suspense>
              ),
            },
            {
              path: ':id',
              element: (
                <Suspense fallback={<Loader />}>
                  <BroadcastScheduleProgramPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          id: MediaManagerRouteEnum.SEGMENTS,
          path: 'segments',
          element: (
            <RequirePlatformRouteAuth permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_SEGMENT]} />
          ),
          children: [
            {
              path: '',
              element: (
                <Suspense fallback={<Loader />}>
                  <SegmentsPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          id: MediaManagerRouteEnum.CLIPS,
          path: 'clips',
          element: <RequirePlatformRouteAuth permissions={[PERMISSIONS.MEDIA_MANAGER.VIEW_CLIP]} />,
          children: [
            {
              path: '',
              element: (
                <Suspense fallback={<Loader />}>
                  <ClipsPage />
                </Suspense>
              ),
            },
          ],
        },
      ],
    },
    {
      id: MediaManagerRouteEnum.PROFILE,
      path: 'profile',
      element: (
        <Suspense fallback={<Loader />}>
          <ProfilePage />
        </Suspense>
      ),
    },
  ],
};
